import { createContext, useContext, useState, useEffect, useRef } from "react";
import { translateBatch, getCached } from "@/i18n/translationService";

export type Lang = "es" | "en";

type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  isTranslating: boolean;
};

const LanguageContext = createContext<Ctx>(null!);

export const LanguageProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [lang, setLangState] = useState<Lang>(() => {
    return (sessionStorage.getItem("app_lang") as Lang) || "es";
  });
  const [isTranslating, setIsTranslating] = useState(false);
  const originalTexts = useRef<Map<Node, string>>(new Map());

  const setLang = (l: Lang) => {
    setLangState(l);
    sessionStorage.setItem("app_lang", l);
  };

  // Funcion que restaura el español original
  const restoreSpanish = () => {
    originalTexts.current.forEach((orig, node) => {
      node.textContent = orig;
    });
  };

  // Funcion que traduce el DOM
  const translateDOM = async (targetLang: Lang) => {
    if (targetLang === "es") {
      restoreSpanish();
      return;
    }

    setIsTranslating(true);
    const root = document.getElementById("root");
    if (!root) return;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const text = node.textContent?.trim() || "";
        const parent = node.parentElement as HTMLElement;
        if (!text) return NodeFilter.FILTER_REJECT;
        if (!parent) return NodeFilter.FILTER_REJECT;

        if (
          ["SCRIPT", "STYLE", "CODE", "INPUT", "TEXTAREA"].includes(
            parent.tagName,
          )
        )
          return NodeFilter.FILTER_REJECT;
        if (parent.closest("[data-no-translate]"))
          return NodeFilter.FILTER_REJECT;
        if (parent.closest("td")) {
          const isHeader = parent.closest("thead") || parent.tagName === "TH";
          if (!isHeader) return NodeFilter.FILTER_REJECT;
        }
        if (/^\$|^\d{4}-\d{2}-\d{2}|^[\d.,]+$/.test(text))
          return NodeFilter.FILTER_REJECT;

        if (text.length < 3) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    const nodes: Text[] = [];
    const textsToTranslate: string[] = [];
    let currentNode = walker.nextNode() as Text | null;

    while (currentNode) {
      const orig = currentNode.textContent?.trim() || "";
      if (!originalTexts.current.has(currentNode)) {
        originalTexts.current.set(currentNode, currentNode.textContent || "");
      }
      // Si ya esta en cache, aplica directo
      const cached = getCached(orig);
      if (cached && targetLang === "en") {
        currentNode.textContent =
          currentNode.textContent?.replace(orig, cached) || cached;
      } else {
        if (!textsToTranslate.includes(orig)) {
          textsToTranslate.push(orig);
        }
        nodes.push(currentNode);
      }
      currentNode = walker.nextNode() as Text | null;
    }

    if (textsToTranslate.length > 0) {
      const translatedMap = await translateBatch(textsToTranslate, targetLang);
      nodes.forEach((node) => {
        const origTrim = node.textContent?.trim() || "";
        // buscamos el original guardado
        const savedOrig = (originalTexts.current.get(node) || "").trim();
        const key =
          textsToTranslate.find(
            (t) => savedOrig.includes(t) || t === origTrim,
          ) || savedOrig;
        if (translatedMap[key] || translatedMap[savedOrig]) {
          node.textContent = translatedMap[key] || translatedMap[savedOrig];
        }
      });
    }
    setIsTranslating(false);
  };

  useEffect(() => {
    document.documentElement.lang = lang;
    // pequeño delay para que renderice la nueva vista antes de traducir
    const timeout = setTimeout(() => translateDOM(lang), 300);

    // Observa cambios de ruta / DOM para traducir vistas nuevas
    const observer = new MutationObserver(() => {
      if (lang !== "es") {
        // debounce
        clearTimeout((observer as any)._t);
        (observer as any)._t = setTimeout(() => translateDOM(lang), 500);
      }
    });

    const root = document.getElementById("root");
    if (root) observer.observe(root, { childList: true, subtree: true });

    return () => {
      clearTimeout(timeout);
      observer.disconnect();
    };
  }, [lang]);

  return (
    <LanguageContext.Provider value={{ lang, setLang, isTranslating }}>
      {children}
      {isTranslating && (
        <div className="fixed bottom-4 right-4 z-[9999] bg-indigo-600 text-white px-3 py-1 rounded-full text-xs shadow-lg">
          Translating...
        </div>
      )}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => useContext(LanguageContext);
